import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { Avatar, IconCollection } from '../index';
import ProfileIconCard from '../ProfileIconCard/ProfileIconCard';

import appSettings from '../../config/settings';

import {
  abilityToSell,
  communityPostAccess,
  CURRENT_USER_BUSINESS_TYPE,
  GROUP_PAYMENT_PAGE_PAYMENT_HISTORY,
  LOCAL_STORAGE_CIRCLE_AUTH_TOKEN,
  PRIMARY_RULEBOOK_GROUP_ID,
  USER_TYPE_YOUTH,
  YOUTH_AGE_LIMIT,
} from '../../util/types';

import {
  currentUserRole,
  getFavoriteRuleBooks,
  getLastThreeRecentChats,
  getUserPermissions,
  cartItemsCount,
  calculateAge,
  isUserSyncShopify,
} from '../../util/data';
import { createResourceLocatorString, pathByRouteName } from '../../util/routes';

import {
  fetchBookMarList,
  fetchUserActivity,
} from '../../containers/CircleCommunityPage/CircleCommunityPage.duck';

import css from './LeftSideDesktopSidebar.module.css';
import { logout } from '../../ducks/auth.duck';
import classNames from 'classnames';

const REDIRECTION_TYPE_SUB_TABS = 'SUB_TABS';
const REDIRECTION_TYPE_LINK = 'LINK';
const REDIRECTION_TYPE_BUTTON = 'BUTTON';

// Returns the tab key for the section in menuData that contains currentPage in its pages array
function getSubMenu(menuData, currentPage) {
  if (!Array.isArray(menuData) || !currentPage) return '';
  for (const section of menuData) {
    if (Array.isArray(section.pages) && section.pages.includes(currentPage)) {
      return section;
    }
  }
  return null;
}

const categories = [
  {
    text: 'Equipment',
    tab: 'REDIRECTION_TYPE_SUB_TABS_EQUIPMENT',
    pageName: 'SearchPage',
    params: {},
    search: {
      hierarchicalCategories: 'Equipment',
    },
    redirectionType: REDIRECTION_TYPE_LINK,
  },
  {
    text: 'For Riding',
    tab: 'REDIRECTION_TYPE_SUB_TABS_FOR RIDING',
    pageName: 'SearchPage',
    params: {},
    search: {
      hierarchicalCategories: 'For Riding',
    },
    redirectionType: REDIRECTION_TYPE_LINK,
  },
  {
    text: 'For The Barn',
    tab: 'REDIRECTION_TYPE_SUB_TABS_FOR THE BARN',
    pageName: 'SearchPage',
    params: {},
    search: {
      hierarchicalCategories: 'For The Barn',
    },
    redirectionType: REDIRECTION_TYPE_LINK,
  },
  {
    text: 'For The Horse',
    tab: 'REDIRECTION_TYPE_SUB_TABS_FOR THE HORSE',
    pageName: 'SearchPage',
    params: {},
    search: {
      hierarchicalCategories: 'For The Horse',
    },
    redirectionType: REDIRECTION_TYPE_LINK,
  },
  {
    text: 'For The Rider',
    tab: 'REDIRECTION_TYPE_SUB_TABS_FOR THE RIDER',
    pageName: 'SearchPage',
    params: {},
    search: {
      hierarchicalCategories: 'For The Rider',
    },
    redirectionType: REDIRECTION_TYPE_LINK,
  },
  {
    text: 'Podcasts',
    tab: 'REDIRECTION_TYPE_SUB_TABS_PODCASTS',
    pageName: 'SearchPage',
    params: {},
    search: {
      hierarchicalCategories: 'Podcasts',
    },
    redirectionType: REDIRECTION_TYPE_LINK,
  },
  {
    text: 'Services',
    tab: 'REDIRECTION_TYPE_SUB_TABS_SERVICES',
    pageName: 'SearchPage',
    params: {},
    search: {
      hierarchicalCategories: 'Services',
    },
    redirectionType: REDIRECTION_TYPE_LINK,
  },
  {
    text: 'Other',
    tab: 'REDIRECTION_TYPE_SUB_TABS_OTHER',
    pageName: 'SearchPage',
    params: {},
    search: {
      hierarchicalCategories: 'Other',
    },
    redirectionType: REDIRECTION_TYPE_LINK,
  },
];

const LeftSideDesktopSidebar = ({
  setIsBookmarkListRequested,
  setIsMyActivityModalOpen,
  onOpenRuleBookModal,
  currentPage,
}) => {
  const intl = useIntl();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const dispatch = useDispatch();
  const state = useSelector(state => state);

  const {
    currentUser,
    currentUserNotificationCount,
    subscriptionPermissionRequest,
    currentUserNotificationCountForOffer,
    topbarGroups,
    userModeratorsGroups,
  } = state.user;

  const spaceListData = Array.isArray(topbarGroups) && topbarGroups.length > 0 ? topbarGroups : [];
  const { currentCommunityMember, onRequestGroupClick } = state.CircleCommunityPage;
  const notificationCount = currentUserNotificationCount || currentUserNotificationCountForOffer;
  const { userFavChatDetails, chatDetails } = state.RuleBooksSearchPage;

  const { id: community_member_id } = currentCommunityMember || {};

  // Logout function
  const onLogout = () => {
    // Perform logout actions
    dispatch(logout()).then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      try {
        typeof window !== 'undefined' && localStorage.removeItem('authCred');
        typeof window !== 'undefined' && localStorage.removeItem(LOCAL_STORAGE_CIRCLE_AUTH_TOKEN);
      } catch (e) {
        console.log(e, '&&& &&& => e');
      }

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }

      console.log('logged out'); // eslint-disable-line
    });
  };

  const userId = currentUser?.id?.uuid;
  const { publicData, protectedData, privateData } =
    (currentUser && currentUser.id && currentUser.attributes?.profile) || {};
  const { userRole, parentId = false } = publicData || {};
  const { subscriptionData = false } = protectedData || {};
  const {
    isOrganizationAdmin,
    isSupabaseAdmin,
    isWalletAdmin,
    isModAdmin,
    isUserAdmin,
    cart,
    isCategoryAdmin,
    isShopifyAdmin,
    isHighlightAdmin,
    isDiscoverGroupsCarouselAdmin,
    isDiscountAdmin,
    isDailyDealsAdmin,
    isSubscriptionAdmin,
  } = privateData || {};
  const userSubscriptionRole = currentUserRole(currentUser);
  const userAbilityToSell = getUserPermissions(userSubscriptionRole, abilityToSell);
  const checkCreatePostAuth = getUserPermissions(userSubscriptionRole, communityPostAccess);
   const isShopifySyncEnabled = isUserSyncShopify(currentUser);

  const cartCount = cartItemsCount(cart) || 0;

  const isSeller =
    userRole !== USER_TYPE_YOUTH && (subscriptionData || !parentId) && userAbilityToSell;
  const isYouthRestricted = userRole === USER_TYPE_YOUTH && parentId && !subscriptionData;

  const favouriteRuleBook = getFavoriteRuleBooks(userFavChatDetails);
  const recentChats = getLastThreeRecentChats(chatDetails);

  const isBusiness = publicData?.currentUserType === CURRENT_USER_BUSINESS_TYPE;

  // Calculate user age from DOB
  const userDob = publicData?.dob;
  const userAge = userDob ? calculateAge(userDob) : null;
  const isUserAdult = userAge !== null && userAge >= YOUTH_AGE_LIMIT;

  // Check if user should see upgrade button (16+ and not basic role)
  const shouldShowUpgradeButton = isUserAdult && userRole !== 'basic';

  // states
  // Single state to track expanded tab: null, 'COMMUNITY_MY_GROUP', or 'COMMUNITY_MODERATED_GROUP'
  const [expandedTab, setExpandedTab] = useState(null);
  const [joinedGroupsQuery, setJoinedGroupsQuery] = useState('');

  const aiFavouriteRuleBook = favouriteRuleBook.map(st => {
    return {
      text: st.rulebook_name,
      tab: `COMMUNITY_AI_FAVOURITE_${st.id}`,
      pageName: 'AiChatBotRulebookPage',
      params: { rulebookId: st.rulebook_id, sessionId: uuidv4() },
      search: {},
      redirectionType: REDIRECTION_TYPE_LINK,
      Icon: <IconCollection name={'roundIcon'} />,
    };
  });

  const aiRecentRuleBook = recentChats.map(st => {
    return {
      text: st.query,
      tab: `COMMUNITY_AI_RECENT_${st.id}`,
      pageName: 'AiChatBotRulebookPage',
      params: { rulebookId: st.rulebook_id, sessionId: st.sessionId },
      search: {},
      redirectionType: REDIRECTION_TYPE_LINK,
      Icon: <IconCollection name={'recentRoundIcon'} />,
    };
  });

  const moderatorGroupsRaw = Array.isArray(spaceListData)
    ? spaceListData
        .filter(item => {
          const isModerator =
            Array.isArray(userModeratorsGroups) &&
            userModeratorsGroups.some(st => st.status == 'active' && st.groupId == item.id);
          const canManage = item.is_member;
          return isModerator && canManage;
        })
        .map(st => {
          return {
            text: st.name,
            tab: `COMMUNITY_GROUP_EXPLORE_${st.id}`,
            pageName: 'JoinedGroupsCommunityPage',
            params: { id: st.id, slug: st.slug },
            search: {},
            redirectionType: REDIRECTION_TYPE_LINK,
            Icon: <ProfileIconCard type="green" />,
            notificationCount: st.pendingCount || 0,
          };
        })
    : [];

  const moderatorGroups =
    moderatorGroupsRaw.length > 4 && expandedTab !== 'COMMUNITY_GROUP_EXPLORE_SEE_MORE'
      ? [
          ...moderatorGroupsRaw.slice(0, 4),
          {
            text: intl.formatMessage({
              id: 'MobileHamburgerNavigation.SEE_MORE',
              defaultMessage: 'See more',
            }),
            tab: 'COMMUNITY_GROUP_EXPLORE_SEE_MORE',
            type: 'Button',
          },
        ]
      : moderatorGroupsRaw.length > 4
      ? [
          ...moderatorGroupsRaw,
          {
            text: intl.formatMessage({
              id: 'MobileHamburgerNavigation.SEE_LESS',
              defaultMessage: 'See less',
            }),
            tab: 'COMMUNITY_MY_GROUP_SEE_MORE',
            type: 'Button',
          },
        ]
      : moderatorGroupsRaw;

  const JoinedGroupsRaw = Array.isArray(spaceListData)
    ? spaceListData
        .filter(item => {
          const isModerator =
            Array.isArray(userModeratorsGroups) &&
            userModeratorsGroups.some(st => st.status == 'active' && st.groupId == item.id);
          const canManage = item.is_member;
          return !isModerator && canManage;
        })
        .map(st => {
          return {
            text: st.name,
            tab: `COMMUNITY_MY_GROUP_${st.id}`,
            pageName: 'JoinedGroupsCommunityPage',
            params: { id: st.id, slug: st.slug },
            search: {},
            redirectionType: REDIRECTION_TYPE_LINK,
            Icon: <ProfileIconCard type="orange" />,
          };
        })
    : [];

  // Apply search filter to JoinedGroupsRaw for desktop
  const JoinedGroupsFiltered =
    typeof joinedGroupsQuery === 'string' && joinedGroupsQuery.trim()
      ? JoinedGroupsRaw.filter(item =>
          (item.text || '').toLowerCase().includes(joinedGroupsQuery.toLowerCase())
        )
      : JoinedGroupsRaw;

  // Check if we should show "No Result" message for desktop
  const shouldShowNoResult = joinedGroupsQuery.trim() && JoinedGroupsFiltered.length === 0;

  const JoinedGroups = shouldShowNoResult
    ? [
        {
          text: intl.formatMessage({
            id: 'MobileHamburgerNavigation.NO_GROUPS_FOUND',
            defaultMessage: 'No groups found',
          }),
          tab: 'NO_RESULT',
          type: 'NoResult',
        },
      ]
    : JoinedGroupsFiltered.length > 4 && expandedTab !== 'COMMUNITY_MY_GROUP_SEE_MORE'
    ? [
        ...JoinedGroupsFiltered.slice(0, 4),
        {
          text: intl.formatMessage({
            id: 'MobileHamburgerNavigation.SEE_MORE',
            defaultMessage: 'See more',
          }),
          tab: 'COMMUNITY_MY_GROUP_SEE_MORE',
          type: 'Button',
        },
      ]
    : JoinedGroupsFiltered.length > 4
    ? [
        ...JoinedGroupsFiltered,
        {
          text: intl.formatMessage({
            id: 'MobileHamburgerNavigation.SEE_LESS',
            defaultMessage: 'See less',
          }),
          tab: 'COMMUNITY_MY_GROUP_SEE_MORE',
          type: 'Button',
        },
      ]
    : JoinedGroupsFiltered;

  const menuData = [
    {
      Icon: <ProfileIconCard type="community_bottom_mobile_svg" />,
      tab: 'COMMUNITY',
      label: intl.formatMessage({ id: 'MobileHamburgerNavigation.COMMUNITY' }),
      subLabel: intl.formatMessage({ id: 'MobileHamburgerNavigation.COMMUNITY_SUB' }),
      redirectionType: REDIRECTION_TYPE_LINK,
      pageName: 'CircleCommunityPage',
      params: {},
      search: {},
      tabLevel: 1,
      subTabs: [
        {
          Icon: <ProfileIconCard type="community_feed_tab" />,
          text: intl.formatMessage({ id: 'MobileHamburgerNavigation.COMMUNITY_MY_FEED' }),
          tab: 'COMMUNITY_FEED_TAB',
          redirectionType: REDIRECTION_TYPE_LINK,
          pageName: 'CircleCommunityPage',
          params: {},
          search: {},
        },
        {
          Icon: <ProfileIconCard type="community_explore" />,
          text: intl.formatMessage({ id: 'MobileHamburgerNavigation.COMMUNITY_EXPLORE' }),
          tab: 'COMMUNITY_EXPLORE',
          redirectionType: REDIRECTION_TYPE_LINK,
          pageName: 'GroupSearchPage',
          params: {},
          search: {},
        },
        ...(userId
          ? [
              {
                Icon: <ProfileIconCard type="community_saved_post" />,
                text: intl.formatMessage({ id: 'MobileHamburgerNavigation.COMMUNITY_SAVED_POST' }),
                tab: 'COMMUNITY_SAVED_POST',
                redirectionType: REDIRECTION_TYPE_BUTTON,
                onClick: () => {
                  setIsBookmarkListRequested(true);
                  dispatch(fetchBookMarList({}));
                },
              },
              {
                Icon: <ProfileIconCard type="community_activity" />,
                text: intl.formatMessage({ id: 'MobileHamburgerNavigation.COMMUNITY_ACTIVITY' }),
                tab: 'COMMUNITY_ACTIVITY',
                redirectionType: REDIRECTION_TYPE_BUTTON,
                onClick: () => {
                  if (!community_member_id) return;
                  setIsMyActivityModalOpen(true);
                  dispatch(fetchUserActivity({ community_member_id, fetchTab: 'posts' }));
                },
              },
            ]
          : []),
        {
          text: intl.formatMessage({
            id: 'MobileHamburgerNavigation.COMMUNITY_REQUEST_GROUP_BUTTON',
          }),
          tab: 'COMMUNITY_REQUEST_GROUP_BUTTON',
          pageName: false,
          type: 'Button',
          disabled: false,
          redirectionType: REDIRECTION_TYPE_BUTTON,
          onClick: () => {
            if (!userId) {
              return history.push('/login');
            }
            if (!checkCreatePostAuth) {
              dispatch(subscriptionPermissionRequest(true));
              return;
            }
            onRequestGroupClick(true);
          },
        },
        {
          text: '',
          tab: 'UNDERLINE',
        },
        ...(userId
          ? [
              {
                text: intl.formatMessage({
                  id: 'MobileHamburgerNavigation.COMMUNITY_MODERATED_GROUP',
                }),
                tab: 'COMMUNITY_MODERATED_GROUP',
                redirectionType: REDIRECTION_TYPE_SUB_TABS,
                subTabs: moderatorGroups,
                isLast: true,
                tabLevel: 2,
              },
              {
                text: '',
                tab: 'UNDERLINE',
              },
              {
                text: intl.formatMessage({ id: 'MobileHamburgerNavigation.COMMUNITY_MY_GROUP' }),
                tab: 'COMMUNITY_MY_GROUP',
                pageName: false,
                subTabs: JoinedGroups,
                searchControl: true,
                isLast: true,
                tabLevel: 2,
              },
            ]
          : [
              {
                Icon: <ProfileIconCard type="join_now" />,
                tab: 'JOIN_NOW',
                text: intl.formatMessage({ id: 'MobileHamburgerNavigation.JOIN_NOW' }),
                pageName: 'SignupPage',
                params: {},
                search: {},
                redirectionType: REDIRECTION_TYPE_LINK,
              },
            ]),
      ],
      pages: [
        'CircleCommunityPage',
        'ExploreCommunityPage',
        'MyGroupCommunityPage',
        'DiscoverGroupsCommunityPage',
        'JoinedGroupsCommunityPage',
        'PostViewPage',
        'GroupSearchPage',
      ],
    },
    {
      Icon: <ProfileIconCard type="marketplace_bottom_mobile_svg" />,
      tab: 'MARKETPLACE',
      label: intl.formatMessage({ id: 'MobileHamburgerNavigation.MARKETPLACE' }),
      subLabel: intl.formatMessage({ id: 'MobileHamburgerNavigation.MARKETPLACE_SUB' }),
      redirectionType: REDIRECTION_TYPE_LINK,
      pageName: 'SearchPage',
      params: {},
      search: {},
      tabLevel: 1,
      subTabs: [
        {
          Icon: <ProfileIconCard type="marketplace_search_page" />,
          text: intl.formatMessage({ id: 'MobileHamburgerNavigation.MARKETPLACE_SEARCH' }),
          tab: 'MARKETPLACE_SERACH_PAGE',
          pageName: 'SearchPage',
          params: {},
          search: {},
          redirectionType: REDIRECTION_TYPE_LINK,
        },
        ...(!isYouthRestricted
          ? [
              {
                Icon: <ProfileIconCard type="marketplace_cart_page" />,
                text: intl.formatMessage({ id: 'MobileHamburgerNavigation.MARKETPLACE_CART_PAGE' }),
                tab: 'MARKETPLACE_CART_PAGE',
                notificationCount: cartCount,
                pageName: 'CartPage',
                params: {},
                search: {},
                redirectionType: REDIRECTION_TYPE_LINK,
              },
            ]
          : []),
        ...(userAbilityToSell || isShopifySyncEnabled
          ? [
              {
                Icon: <ProfileIconCard type="marketplace_manage_listings_page" />,
                text: intl.formatMessage({
                  id: 'MobileHamburgerNavigation.MARKETPLACE_MANAGE_LISTINGS_PAGE',
                }),
                tab: 'MARKETPLACE_MANAGE_LISTINGS_PAGE',
                pageName: 'ManageListingsPage',
                params: {},
                search: {},
                redirectionType: REDIRECTION_TYPE_LINK,
                isActive: currentPage == 'ManageListingsPage',
              },
            ]
          : []),
        ...(!isYouthRestricted && userId
          ? [
              {
                Icon: <ProfileIconCard type="marketplace_inbox_base_page" />,
                text: intl.formatMessage({
                  id: 'MobileHamburgerNavigation.MARKETPLACE_INBOX_BASE_PAGE',
                }),
                tab: 'MARKETPLACE_INBOX_BASE_PAGE',
                pageName: 'InboxBasePage',
                notificationCount: notificationCount,
                params: {},
                search: {},
                redirectionType: REDIRECTION_TYPE_LINK,
              },
            ]
          : []),
        {
          text: intl.formatMessage({ id: 'MobileHamburgerNavigation.MARKETPLACE_POST_ITEM' }),
          tab: 'MARKETPLACE_POST_ITEM',
          type: 'Button',
          disabled: !userAbilityToSell,
          onClick: () => {
            return history.push(
              createResourceLocatorString('NewListingPage', routeConfiguration, {}, {})
            );
          },
          redirectionType: REDIRECTION_TYPE_BUTTON,
        },
        {
          text: '',
          tab: 'UNDERLINE',
        },
        {
          text: intl.formatMessage({ id: 'MobileHamburgerNavigation.MARKETPLACE_MAIN_CATEGORIES' }),
          tab: 'MARKETPLACE_MAIN_CATEGORIES',
          redirectionType: REDIRECTION_TYPE_SUB_TABS,
          subTabs: categories,
          isLast: true,
          tabLevel: 2,
        },
      ],
      pages: [
        'SearchPage',
        'ListingPage',
        'CheckoutPage',
        'ListingPageVariant',
        'EditListingPage',
        'ListingPageCanonical',
        'InboxPage',
        'TransactionPage',
        'OrderDetailsPage',
        'SaleDetailsPage',
        'ManageListingsPage',
        'CartPage',
        'SubscriptionCheckoutPage',
        'GroupPaymentSuccessPage',
      ],
    },
    {
      Icon: <ProfileIconCard type="turnoutai_bottom_mobile_svg" />,
      tab: 'AI',
      label: intl.formatMessage({ id: 'MobileHamburgerNavigation.AI' }),
      subLabel: intl.formatMessage({ id: 'MobileHamburgerNavigation.AI_SUB' }),
      redirectionType: REDIRECTION_TYPE_SUB_TABS,
      tabLevel: 1,
      subTabs: [
        {
          Icon: <ProfileIconCard type="marketplace_search_page" />,
          text: intl.formatMessage({ id: 'MobileHamburgerNavigation.AI_RULEBOOKS' }),
          tab: 'AI_RULEBOOKS',
          pageName: 'RuleBooksSearchPage',
          params: { rulebookId: 'all' },
          search: {},
          redirectionType: REDIRECTION_TYPE_LINK,
        },
        {
          Icon: <ProfileIconCard type="community_saved_post" />,
          text: intl.formatMessage({ id: 'MobileHamburgerNavigation.AI_FAVOURITES' }),
          tab: 'AI_EXPLORE',
          pageName: 'FavouritePage',
          params: { rulebookId: 'favourites' },
          search: {},
          redirectionType: REDIRECTION_TYPE_LINK,
        },
        userId
          ? [
              {
                Icon: <ProfileIconCard type="ai_turnouthq_chat_bot" />,
                text: intl.formatMessage({ id: 'MobileHamburgerNavigation.AI_TURNOUTHQ_CHAT_BOT' }),
                tab: 'AI_TURNOUTHQ_CHAT_BOT',
                pageName: 'AiChatBotRulebookPage',
                params: { rulebookId: PRIMARY_RULEBOOK_GROUP_ID, sessionId: uuidv4() },
                search: {},
                redirectionType: REDIRECTION_TYPE_LINK,
              },
            ]
          : [],
        {
          text: intl.formatMessage({ id: 'MobileHamburgerNavigation.AI_REQUEST_RULEBOOK_BUTTON' }),
          tab: 'AI_REQUEST_RULEBOOK_BUTTON',
          pageName: false,
          type: 'Button',
          disabled: false,
          onClick: () => {
            if (!userId) {
              return history.push('/login');
            }
            onOpenRuleBookModal();
          },
          redirectionType: REDIRECTION_TYPE_BUTTON,
        },
        {
          text: '',
          tab: 'UNDERLINE',
        },
        ...(userId
          ? [
              {
                text: intl.formatMessage({ id: 'MobileHamburgerNavigation.AI_MY_FAVORITES' }),
                tab: 'AI_MY_FAVORITES',
                redirectionType: REDIRECTION_TYPE_SUB_TABS,
                subTabs: aiFavouriteRuleBook,
                isLast: true,
                tabLevel: 2,
              },

              {
                text: '',
                tab: 'UNDERLINE',
              },

              {
                text: intl.formatMessage({ id: 'MobileHamburgerNavigation.AI_GROUP' }),
                tab: 'AI_GROUP',
                redirectionType: REDIRECTION_TYPE_SUB_TABS,
                isLast: true,
                subTabs: aiRecentRuleBook,
                tabLevel: 2,
              },
            ]
          : [
              {
                Icon: <ProfileIconCard type="join_now" />,
                tab: 'JOIN_NOW',
                text: intl.formatMessage({ id: 'MobileHamburgerNavigation.JOIN_NOW' }),
                pageName: 'SignupPage',
                params: {},
                search: {},
                redirectionType: REDIRECTION_TYPE_LINK,
              },
            ]),
      ],
      pages: ['AiChatBotPage', 'AiChatBotRulebookPage', 'RuleBooksSearchPage', 'FavouritePage'],
    },
    {
      Icon: <ProfileIconCard type="directory" />,
      tab: 'DIRECTORY',
      label: intl.formatMessage({ id: 'MobileHamburgerNavigation.DIRECTORY' }),
      redirectionType: REDIRECTION_TYPE_LINK,
      pageName: 'AlgoliaDirectorySearchPage',
      params: {},
      search: {},
      subTabs: [],
      pages: ['AlgoliaDirectorySearchPage'],
    },
    {
      Icon: <Avatar className={css.avatar} user={currentUser} disableProfileLink />,
      tab: 'PROFILE',
      label: intl.formatMessage({ id: 'MobileHamburgerNavigation.PROFILE' }),
      subLabel: intl.formatMessage({ id: 'MobileHamburgerNavigation.PROFILE_SUB' }),
      redirectionType: REDIRECTION_TYPE_LINK,
      pageName: 'ProfilePage',
      params: { id: userId },
      search: {},
      tabLevel: 1,
      subTabs: [
        {
          Icon: <Avatar className={css.avatar} user={currentUser} disableProfileLink />,
          text: intl.formatMessage({ id: 'MobileHamburgerNavigation.PROFILE_VIEW_PROFILE' }),
          tab: 'PROFILE_VIEW_PROFILE_PAGE',
          pageName: 'ProfilePage',
          params: { id: userId },
          search: {},
          redirectionType: REDIRECTION_TYPE_LINK,
          isActive: currentPage == 'ProfilePage',
        },
        {
          Icon: <ProfileIconCard type="community_feed_tab" />,
          text: intl.formatMessage({ id: 'MobileHamburgerNavigation.PROFILE_EDIT' }),
          tab: 'PROFILE_PROFILE_SETTINGS_PAGE',
          pageName: 'ProfileSettingsPage',
          params: {},
          search: {},
          redirectionType: REDIRECTION_TYPE_LINK,
          isActive: currentPage == 'ProfileSettingsPage',
        },
        {
          Icon: <ProfileIconCard type="shop" />,
          text: intl.formatMessage({ id: 'MobileHamburgerNavigation.PROFILE_MEMBERSHIP' }),
          tab: 'PROFILE_MEMBERSHIP',
          pageName: 'StripeSubscriptionPage',
          params: {},
          search: {},
          redirectionType: REDIRECTION_TYPE_LINK,
          isActive: currentPage == 'StripeSubscriptionPage',
        },
        {
          Icon: <ProfileIconCard type="youth_badge" />,
          text: intl.formatMessage({
            id: 'MobileHamburgerNavigation.PROFILE_ACCOUNT_SETTINGS_YOUTH_ACCOUNT_PAGE',
          }),
          tab: 'PROFILE_ACCOUNT_SETTINGS_YOUTH_ACCOUNT_PAGE',
          pageName: 'YouthAccountSettingsPage',
          params: {},
          search: {},
          redirectionType: REDIRECTION_TYPE_LINK,
          isActive: currentPage == 'YouthAccountSettingsPage',
        },
        {
          text: intl.formatMessage({ id: 'MobileHamburgerNavigation.PROFILE_LOG_OUT' }),
          tab: 'PROFILE_REQUEST_GROUP_BUTTON',
          pageName: false,
          type: 'Button',
          disabled: false,
          onClick: () => {
            onLogout();
          },
          redirectionType: REDIRECTION_TYPE_BUTTON,
        },
        ...(shouldShowUpgradeButton
          ? [
              {
                text: intl.formatMessage({ id: 'MobileHamburgerNavigation.PROFILE_UPGRADE' }),
                tab: 'PROFILE_UPGRADE',
                pageName: 'StripeSubscriptionPage',
                params: {},
                search: {},
                type: 'UpgradeButton',
                redirectionType: REDIRECTION_TYPE_LINK,
                isActive: currentPage == 'StripeSubscriptionPage',
              },
            ]
          : []),
        {
          text: '',
          tab: 'UNDERLINE',
        },
        {
          text: intl.formatMessage({
            id: 'MobileHamburgerNavigation.PROFILE_ACCOUNT_SETTINGS_PAGE',
          }),
          tab: 'PROFILE_ACCOUNT_SETTINGS_PAGE',
          redirectionType: REDIRECTION_TYPE_SUB_TABS,
          isLast: true,
          tabLevel: 2,
          subTabs: [
            {
              text: intl.formatMessage({
                id: 'MobileHamburgerNavigation.PROFILE_ACCOUNT_SETTINGS_CONTACT_DETAILS',
              }),
              tab: 'PROFILE_ACCOUNT_SETTINGS_CONTACT_DETAIL_PAGE',
              pageName: 'ContactDetailsPage',
              params: {},
              search: {},
              redirectionType: REDIRECTION_TYPE_LINK,
              isActive: currentPage == 'ContactDetailsPage',
            },
            {
              text: intl.formatMessage({
                id: 'MobileHamburgerNavigation.PROFILE_ACCOUNT_SETTINGS_PASSWORD_CHANGE_PAGE',
              }),
              tab: 'PROFILE_ACCOUNT_SETTINGS_PASSWORD_CHANGE_PAGE',
              pageName: 'PasswordChangePage',
              params: {},
              search: {},
              redirectionType: REDIRECTION_TYPE_LINK,
              isActive: currentPage == 'PasswordChangePage',
            },
            // ...(isBusiness ? [{
            //   text: intl.formatMessage({ id: "MobileHamburgerNavigation.PROFILE_PROFILE_GENERAL_HOURS_PAGE" }),
            //   tab: "PROFILE_PROFILE_GENERAL_HOURS_PAGE",
            //   pageName: "GeneralHourPage",
            //   params: {},
            //   search: {},
            //   redirectionType: REDIRECTION_TYPE_LINK,
            //   isActive: currentPage == "GeneralHourPage",
            // }] : []),
            ...(isSeller
              ? [
                  {
                    text: intl.formatMessage({
                      id: 'MobileHamburgerNavigation.PROFILE_ACCOUNT_SETTINGS_SHIPPING_PAGE',
                    }),
                    tab: 'PROFILE_ACCOUNT_SETTINGS_SHIPPING_PAGE',
                    pageName: 'ShippingInfoPage',
                    params: {},
                    search: {},
                    redirectionType: REDIRECTION_TYPE_LINK,
                    isActive: currentPage == 'ShippingInfoPage',
                  },
                  {
                    text: intl.formatMessage({
                      id: 'MobileHamburgerNavigation.PROFILE_ACCOUNT_SETTINGS_PAYOUT_PAGE',
                    }),
                    tab: 'PROFILE_ACCOUNT_SETTINGS_PAYOUT_PAGE',
                    pageName: 'StripePayoutPage',
                    params: {},
                    search: {},
                    redirectionType: REDIRECTION_TYPE_LINK,
                    isActive: currentPage == 'StripePayoutPage',
                  },
                  {
                    text: intl.formatMessage({
                      id: 'MobileHamburgerNavigation.PROFILE_ACCOUNT_SETTINGS_PAYMENT_METHOD_PAGE',
                    }),
                    tab: 'PROFILE_ACCOUNT_SETTINGS_PAYMENT_METHOD_PAGE',
                    pageName: 'PaymentMethodsPage',
                    params: {},
                    search: {},
                    redirectionType: REDIRECTION_TYPE_LINK,
                    isActive: currentPage == 'PaymentMethodsPage',
                  },
                ]
              : []),
            // Group Payment History
            {
              text: intl.formatMessage({
                id: 'MobileHamburgerNavigation.PROFILE_ACCOUNT_SETTINGS_GROUP_PAYMENT_HISTORY_PAGE',
              }),
              tab: 'PROFILE_ACCOUNT_SETTINGS_GROUP_PAYMENT_HISTORY_PAGE',
              pageName: 'GroupPaymentHistoryPage',
              params: { tab: GROUP_PAYMENT_PAGE_PAYMENT_HISTORY },
              search: {},
              redirectionType: REDIRECTION_TYPE_LINK,
              isActive: currentPage == 'GroupPaymentHistoryPage',
            },
            {
              text: intl.formatMessage({
                id: 'MobileHamburgerNavigation.PROFILE_ACCOUNT_SETTINGS_DELETE_PAGE',
              }),
              tab: 'PROFILE_ACCOUNT_SETTINGS_DELETE_PAGE',
              pageName: 'DeleteAccountPage',
              params: {},
              search: {},
              redirectionType: REDIRECTION_TYPE_LINK,
              isActive: currentPage == 'DeleteAccountPage',
            },
          ],
        },
        ...(isOrganizationAdmin ||
        isSupabaseAdmin ||
        isWalletAdmin ||
        isModAdmin ||
        isUserAdmin ||
        isShopifyAdmin ||
        isHighlightAdmin ||
        isDiscoverGroupsCarouselAdmin ||
        isDiscountAdmin ||
        isDailyDealsAdmin ||
        isSubscriptionAdmin
          ? [
              {
                text: '',
                tab: 'UNDERLINE',
              },
              {
                text: intl.formatMessage({ id: 'MobileHamburgerNavigation.PROFILE_ADMIN_PAGE' }),
                tab: 'PROFILE_ADMIN_PAGE',
                redirectionType: REDIRECTION_TYPE_SUB_TABS,
                isLast: true,
                tabLevel: 2,
                subTabs: [
                  ...(isOrganizationAdmin
                    ? [
                        {
                          text: intl.formatMessage({
                            id:
                              'LayoutWrapperAccountSettingsSideNav.AdminPanelOrganisationPageTabTitle',
                          }),
                          pageName: 'AdminPanelOrganisationPage',
                          params: {},
                          search: {},
                          redirectionType: REDIRECTION_TYPE_LINK,
                          isActive: currentPage == 'AdminPanelOrganisationPage',
                        },
                      ]
                    : []),
                  ...(isSupabaseAdmin
                    ? [
                        {
                          text: intl.formatMessage({ id: 'TopbarDesktop.adminPanelLink' }),
                          tab: 'PROFILE_ADMIN_PAGE_SUPABASE',
                          pageName: 'AdminPanelSupabasePage',
                          params: { tab: 'general' },
                          search: {},
                          redirectionType: REDIRECTION_TYPE_LINK,
                          isActive: currentPage == 'AdminPanelSupabasePage',
                        },
                      ]
                    : []),
                  ...(isModAdmin
                    ? [
                        {
                          text: intl.formatMessage({ id: 'TopbarDesktop.adminPanelModeratorLink' }),
                          tab: 'PROFILE_ADMIN_PAGE_MODERATOR',
                          pageName: 'AdminPanelModeratorsPage',
                          params: { tab: 'general' },
                          search: {},
                          redirectionType: REDIRECTION_TYPE_LINK,
                          isActive: currentPage == 'AdminPanelModeratorsPage',
                        },
                      ]
                    : []),
                  ...(isWalletAdmin
                    ? [
                        {
                          text: intl.formatMessage({
                            id: 'LayoutWrapperAccountSettingsSideNav.AdminPanelWalletPage',
                          }),
                          tab: 'PROFILE_ADMIN_PAGE_WALLET',
                          pageName: 'AdminPanelWalletPage',
                          params: {},
                          search: {},
                          redirectionType: REDIRECTION_TYPE_LINK,
                          isActive: currentPage == 'AdminPanelWalletPage',
                        },
                      ]
                    : []),
                  // ...(isShopifyAdmin
                  //   ? [
                  //       {
                  //         text: intl.formatMessage({
                  //           id:
                  //             'LayoutWrapperAccountSettingsSideNav.AdminPanelShopifyFeeReportPage',
                  //         }),
                  //         tab: 'PROFILE_ADMIN_PAGE_SHOPIFY_FEE_REPORT',
                  //         pageName: 'AdminPanelShopifyFeeReportPage',
                  //         params: {},
                  //         search: {},
                  //         redirectionType: REDIRECTION_TYPE_LINK,
                  //         isActive: currentPage == 'AdminPanelShopifyFeeReportPage',
                  //       },
                  //     ]
                  //   : []),
                  ...(isCategoryAdmin
                    ? [
                        {
                          text: intl.formatMessage({
                            id: 'LayoutWrapperAccountSettingsSideNav.AdminPanelCategoryPage',
                          }),
                          tab: 'PROFILE_ADMIN_PAGE_CATEGORY',
                          pageName: 'AdminPanelCategoryPage',
                          params: {},
                          search: {},
                          redirectionType: REDIRECTION_TYPE_LINK,
                          isActive: currentPage == 'AdminPanelCategoryPage',
                        },
                      ]
                    : []),
                  ...(isHighlightAdmin
                    ? [
                        {
                          text: intl.formatMessage({
                            id: 'LayoutWrapperAccountSettingsSideNav.AdminPanelHighlightsPage',
                          }),
                          tab: 'PROFILE_ADMIN_PAGE_HIGHLIGHTS',
                          pageName: 'AdminPanelHighlightsPage',
                          params: {},
                          search: {},
                          redirectionType: REDIRECTION_TYPE_LINK,
                          isActive: [
                            'AdminPanelHighlightsPage',
                            'AdminPanelHighlightCategoryPage',
                          ].includes(currentPage),
                        },
                      ]
                    : []),
                  ...(isDiscoverGroupsCarouselAdmin
                    ? [
                        {
                          text: intl.formatMessage({
                            id:
                              'LayoutWrapperAccountSettingsSideNav.AdminPanelDiscoverGroupsCarouselsPage',
                          }),
                          tab: 'PROFILE_ADMIN_PAGE_DISCOVER_GROUPS_CAROUSELS',
                          pageName: 'AdminPanelDiscoverGroupsCarouselsPage',
                          params: {},
                          search: {},
                          redirectionType: REDIRECTION_TYPE_LINK,
                          isActive: currentPage == 'AdminPanelDiscoverGroupsCarouselsPage',
                        },
                      ]
                    : []),
                  ...(isDiscountAdmin
                    ? [
                        {
                          text: 'Discount Codes',
                          tab: 'PROFILE_ADMIN_PAGE_DISCOUNT_CODES',
                          pageName: 'AdminPanelDiscountCodesPage',
                          params: {},
                          search: {},
                          redirectionType: REDIRECTION_TYPE_LINK,
                          isActive: currentPage == 'AdminPanelDiscountCodesPage',
                        },
                      ]
                    : []),
                  // ...(isDailyDealsAdmin
                  //   ? [
                  //       {
                  //         text: 'Daily Deals',
                  //         tab: 'PROFILE_ADMIN_PAGE_DAILY_DEALS',
                  //         pageName: 'AdminPanelDailyDealsPage',
                  //         params: {},
                  //         search: {},
                  //         redirectionType: REDIRECTION_TYPE_LINK,
                  //         isActive: currentPage == 'AdminPanelDailyDealsPage',
                  //       },
                  //     ]
                  //   : []),
                  ...(isSubscriptionAdmin
                    ? [
                        {
                          text: intl.formatMessage({
                            id: 'LayoutWrapperAccountSettingsSideNav.AdminPanelSubscriptionsPage',
                          }),
                          tab: 'PROFILE_ADMIN_PAGE_SUBSCRIPTIONS',
                          pageName: 'AdminPanelSubscriptionsPage',
                          params: {},
                          search: {},
                          redirectionType: REDIRECTION_TYPE_LINK,
                          isActive: currentPage == 'AdminPanelSubscriptionsPage',
                        },
                      ]
                    : []),
                  ...(isUserAdmin
                    ? [
                        {
                          text: intl.formatMessage({
                            id: 'LayoutWrapperAccountSettingsSideNav.AdminPanelUserPage',
                          }),
                          tab: 'PROFILE_ADMIN_PAGE_USER',
                          pageName: 'AdminPanelUserPage',
                          params: {},
                          search: {},
                          redirectionType: REDIRECTION_TYPE_LINK,
                          isActive: currentPage == 'AdminPanelUserPage',
                        },
                      ]
                    : []),
                ],
              },
            ]
          : []),
      ],
      pages: [
        'ProfileSettingsPage',
        'PasswordRecoveryPage',
        'ContactDetailsPage',
        'PasswordChangePage',
        'ShippingInfoPage',
        'YouthAccountSettingsPage',
        'StripePayoutPage',
        'StripePayoutOnboardingPage',
        'PaymentMethodsPage',
        'StripeSubscriptionPage',
        'GroupPaymentHistoryPage',
        'AdminPanelOrganisationPage',
        'AdminPanelModeratorsPage',
        'AdminPanelUserPage',
        'AdminPanelWalletPage',
        'AdminPanelShopifyFeeReportPage',
        'AdminPanelCategoryPage',
        'AdminPanelDiscountCodesPage',
        'AdminPanelDailyDealsPage',
        'AdminPanelSubscriptionsPage',
        'AdminPanelHighlightsPage',
        'AdminPanelHighlightCategoryPage',
        'AdminPanelDiscoverGroupsCarouselsPage',
        'AdminPanelSupabasePage',
        'DeleteAccountPage',
        'GeneralHourPage',
      ],
    },
  ];

  const navigateToPage = section => {
    const { tab, pageName, params = {}, search = {} } = section;
    if (pageName) {
      history.push(createResourceLocatorString(pageName, routeConfiguration, params, search));
      pageName == 'SearchPage' && search?.hierarchicalCategories && window
        ? window?.location?.reload()
        : null;
      return;
    } else {
      // If the section does not have a pageName, just log the tab
      console.log(`Navigating to tab: ${tab}`);
    }
  };

  const handleMenuClick = section => {
    const { redirectionType, subTabs, onClick, type, isLast = false } = section;

    if (redirectionType === REDIRECTION_TYPE_SUB_TABS && subTabs && subTabs.length && !isLast) {
      return;
    }

    if (redirectionType === REDIRECTION_TYPE_LINK) {
      // If the section is a link, navigate to the corresponding page
      navigateToPage(section);
      return;
    }

    if (redirectionType === REDIRECTION_TYPE_BUTTON && onClick && type != 'button') {
      // If the section is a button, handle the button click
      onClick();
      return;
    }
  };

  const handleButtonClick = item => {
    if (item.onClick) {
      item.onClick();
    }
  };

  const renderDivider = key => <div className={css.underline} key={key} />;

  const renderButton = (item, key) => (
    <button
      className={css.menuButton}
      disabled={item.disabled}
      key={key}
      onClick={() => handleButtonClick(item)}
    >
      {item.text}
    </button>
  );

  const renderNoResult = (item, key) => (
    <div className={css.noResultMessage} key={key}>
      {item.text}
    </div>
  );

  const renderMenuItem = (item, key, isLast) => {
    const deleteItem = item.tab === 'PROFILE_ACCOUNT_SETTINGS_DELETE_PAGE';

    // Special rendering for UpgradeButton - render as button style
    if (item.type === 'UpgradeButton') {
      return (
        <button key={key} className={css.menuButton} onClick={() => handleMenuClick(item)}>
          {typeof item.text === 'string' ? item.text : item.text?.props?.id}
        </button>
      );
    }

    return (
      <React.Fragment key={key}>
        <div
          className={
            item.isActive
              ? css.menusectionActive
              : isLast
              ? css.menuSectionInnerLast
              : css.menuSectionInner
          }
          onClick={() => handleMenuClick(item)}
        >
          {item.Icon ? <div className={css.menuItemIcon}>{item.Icon}</div> : null}
          <div
            className={classNames(
              item.isActive ? css.tabnameisActive : item.subTabs ? css.tabnameActive : css.tabname
            )}
          >
            {typeof item.text === 'string' ? item.text : item.text?.props?.id}
          </div>
          {item.notificationCount ? (
            <div className={css.notificationCount}> {item.notificationCount}</div>
          ) : null}
        </div>
        {item.searchControl ? (
          <div className={css.searchInline} onClick={e => e.stopPropagation()}>
            <input
              className={css.joinedGroupsSearchInputInline}
              placeholder={intl.formatMessage({
                id: 'MobileHamburgerNavigation.SEARCHFORGROUPS',
                defaultMessage: 'Search for groups',
              })}
              value={joinedGroupsQuery}
              onChange={e => setJoinedGroupsQuery(e.target.value)}
            />
          </div>
        ) : null}
        {item.subTabs ? (
          <div className={css.discoverItems}>{renderSubMenu(item.subTabs, true)}</div>
        ) : null}
      </React.Fragment>
    );
  };

  const renderSubMenu = (subTabs, isLast) => (
    <div className={css.subMenu}>
      {subTabs.map((subItem, subKey) => {
        if (subItem.tab === 'UNDERLINE') return renderDivider(subKey);
        // Special handling for 'See more' button in JoinedGroups and moderatorGroups
        if (
          ['COMMUNITY_MY_GROUP_SEE_MORE', 'COMMUNITY_GROUP_EXPLORE_SEE_MORE'].includes(
            subItem.tab
          ) &&
          subItem.type == 'Button'
        ) {
          return (
            <div
              className={css.seeMoreButton}
              onClick={() => {
                if (expandedTab == subItem.tab) setExpandedTab(null);
                else setExpandedTab(subItem.tab);
              }}
              key={'key' + subItem.tab}
            >
              {subItem.text}
            </div>
          );
        }
        // Special handling for UpgradeButton - it should navigate like a link
        if (subItem.type === 'UpgradeButton') {
          return renderMenuItem(subItem, subKey, isLast);
        }
        if (subItem.type === 'Button') return renderButton(subItem, subKey);
        if (subItem.type === 'NoResult') return renderNoResult(subItem, subKey);
        return renderMenuItem(subItem, subKey, isLast);
      })}
    </div>
  );

  const subMenuOpen = getSubMenu(menuData, currentPage);

  return subMenuOpen ? (
    <div className={css.sidebar}>
      {subMenuOpen && subMenuOpen.subLabel ? (
        <div className={css.subMenuOpenSubLabel}>{subMenuOpen.subLabel}</div>
      ) : null}

      {subMenuOpen
        ? renderSubMenu(
            Array.isArray(menuData.find(st => st.tab === subMenuOpen?.tab)?.subTabs)
              ? menuData.find(st => st.tab === subMenuOpen?.tab)?.subTabs
              : []
          )
        : null}
      <div className={css.sidebarFooter}></div>
    </div>
  ) : (
    <></>
  );
};

export default LeftSideDesktopSidebar;
